import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  GripVertical, Eye, EyeOff, Save, RefreshCw, Loader2,
  CheckCircle, AlertTriangle, Sidebar as SidebarIcon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Sortable Module Item Component
 */
const SortableModuleItem = ({ module, onToggleVisibility }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl mb-2 transition-all ${
        isDragging ? 'shadow-lg ring-2 ring-teal-500' : 'hover:shadow-md'
      } ${!module.visible ? 'bg-gray-50' : ''}`}
      data-testid={`sidebar-module-${module.id}`}
    >
      {/* Drag Handle */}
      <button
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 rounded"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={20} />
      </button>

      {/* Order Number */}
      <span className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-600 font-bold text-sm rounded-lg">
        {module.order}
      </span>

      {/* Module Label */}
      <div className="flex-1">
        <p className={`font-medium ${module.visible ? 'text-gray-800' : 'text-gray-400'}`}>
          {module.label}
        </p>
        <p className="text-xs text-gray-400">{module.id}</p>
      </div>

      {/* Visibility Toggle */}
      <button
        onClick={() => onToggleVisibility(module.id)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          module.visible
            ? 'bg-green-50 text-green-600 hover:bg-green-100'
            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
        }`}
        data-testid={`toggle-visibility-${module.id}`}
      >
        {module.visible ? <Eye size={18} /> : <EyeOff size={18} />}
        <span className="text-sm font-medium">
          {module.visible ? 'Visible' : 'Hidden'}
        </span>
      </button>
    </div>
  );
};

/**
 * Sidebar Visibility Settings Page
 * Admin-only page for drag-and-drop sidebar module management
 */
const SidebarSettings = () => {
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/settings/sidebar-modules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load modules');
      const data = await response.json();
      // Sort by order
      const sortedModules = [...(data.modules || [])].sort((a, b) => a.order - b.order);
      setModules(sortedModules);
      setHasChanges(false);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load sidebar modules' });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setModules((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update order values
        return newItems.map((item, index) => ({
          ...item,
          order: index + 1
        }));
      });
      setHasChanges(true);
    }
  };

  const handleToggleVisibility = (moduleId) => {
    setModules((items) =>
      items.map((item) =>
        item.id === moduleId ? { ...item, visible: !item.visible } : item
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE}/api/settings/sidebar-modules`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ modules })
      });

      if (!response.ok) throw new Error('Failed to save');

      setMessage({ type: 'success', text: 'Sidebar settings saved successfully!' });
      setHasChanges(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save sidebar settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sidebar-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <SidebarIcon size={28} className="text-teal-600" />
            Sidebar Visibility Editor
          </h1>
          <p className="text-gray-500 mt-1">
            Drag to reorder modules and toggle visibility
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadModules}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            disabled={loading}
          >
            <RefreshCw size={18} />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-colors ${
              hasChanges
                ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="save-sidebar-settings"
          >
            {saving ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
          message.type === 'error'
            ? 'bg-red-50 border border-red-200 text-red-600'
            : 'bg-green-50 border border-green-200 text-green-600'
        }`}>
          {message.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          <strong>Tip:</strong> Drag modules to reorder them. Click the visibility toggle to show or hide modules in the sidebar. Changes will take effect for all users after saving.
        </p>
      </div>

      {/* Sortable Modules List */}
      <div className="bg-gray-50 rounded-2xl p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={modules.map(m => m.id)}
            strategy={verticalListSortingStrategy}
          >
            {modules.map((module) => (
              <SortableModuleItem
                key={module.id}
                module={module}
                onToggleVisibility={handleToggleVisibility}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Stats */}
      <div className="flex gap-4">
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Eye size={20} className="text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-800">
              {modules.filter(m => m.visible).length}
            </p>
            <p className="text-xs text-gray-500">Visible Modules</p>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <EyeOff size={20} className="text-gray-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-800">
              {modules.filter(m => !m.visible).length}
            </p>
            <p className="text-xs text-gray-500">Hidden Modules</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidebarSettings;
